import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AnalysisView } from "../Components/Analysis/View";
import { Layout } from "../Layouts/Layout";
import { CampaignSelector } from "../Components/Campaign/Selector";
import { TeamSelector } from "../Components/Team/Selector";
import { UserSelector } from "../Components/User/Selector";
import { getUserName } from "../DataTransform";
import {
  User,
  Team,
  ResponseSubjectType,
  Campaign,
  ItemSubjectType,
} from "../types";
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client";
import { InstructorResultsResult, GET_INSTRUCTOR_RESULTS } from "../Queries";
import { SubjectTypeSelector } from "../Components/SubjectType/Selector";
import {
  Button,
  Switch,
  Stack,
  Typography,
  Box,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import isCompatible from "../Components/SubjectType/compatibility";
import { useErrorBoundary } from "../Contexts/ErrorContext";

export const Results = () => {
  const {
    campaignId,
    subjectType = ResponseSubjectType.Peer,
    teamId,
    userId,
  } = useParams();
  const navigate = useNavigate();
  const [t] = useTranslation();
  const { setError } = useErrorBoundary();

  //
  // State
  //

  const [reverse, setReverse] = useState(false);
  const [authorType, setAuthorType] = useState<string | undefined>(undefined);

  //
  // Queries
  //

  // Server-side aggregated results (optionally filtered by team/user)
  const subjectIds = userId ? [userId] : undefined;
  const teamIds = teamId ? [teamId] : undefined;
  const responseSubjectType =
    subjectType === ResponseSubjectType.General
      ? "GL"
      : subjectType === ResponseSubjectType.TeamInstructors
        ? "TI"
        : undefined;
  const {
    loading: instructorResultsLoading,
    data: instructorResultsData,
    previousData: instructorResultsPreviousData,
  } = useQuery<InstructorResultsResult>(GET_INSTRUCTOR_RESULTS, {
    variables: {
      id: campaignId,
      subjectIds,
      teamIds,
      responseSubjectType,
      authorType: authorType || undefined,
    },
    fetchPolicy: "network-only",
    onError: (error) => setError(t("errors.results.list"), error),
  });

  // Use previousData while a refetch is in-flight so hasMixedAuthors doesn't
  // flicker to false and incorrectly reset the author filter.
  const effectiveResultsData =
    instructorResultsData ?? instructorResultsPreviousData;
  const hasMixedAuthors =
    effectiveResultsData?.instructorResults.hasMixedAuthors === true;
  const hasMultipleRaterTypes =
    effectiveResultsData?.instructorResults.hasMultipleRaterTypes === true;

  // Reset authorType when the campaign no longer has mixed authors
  useEffect(() => {
    if (!hasMixedAuthors && !instructorResultsLoading) {
      setAuthorType(undefined);
    }
  }, [hasMixedAuthors, instructorResultsLoading]);

  // Prefer teams from instructorResults; fallback to campaign.groups
  const campaign: Campaign | null =
    effectiveResultsData?.instructorResults.campaign || null;
  const teams: Team[] =
    effectiveResultsData?.instructorResults.availableTeams || [];
  const team = teams.find((target) => target.id === teamId);

  // Prefer campaigns from instructorResults; fallback to campaigns query
  const campaigns: { id: string; name: string }[] =
    effectiveResultsData?.instructorResults.availableCampaigns || [];

  const template =
    campaign?.form || effectiveResultsData?.instructorResults.form;
  const precomputedAnalyses =
    effectiveResultsData?.instructorResults.analyses || [];
  const precomputedAggregates =
    effectiveResultsData?.instructorResults.aggregates || [];
  const precomputedPointRows = (
    effectiveResultsData?.instructorResults.pointRows || []
  ).map((r: any) => ({
    ...r,
    label: r.question?.label,
  }));
  const studentAverages =
    effectiveResultsData?.instructorResults.studentAverages || [];
  const teamAverages =
    effectiveResultsData?.instructorResults.teamAverages || [];
  const users: User[] =
    effectiveResultsData?.instructorResults.availableUsers || [];
  const filteredUsers = users;
  const selectedUser: User | undefined = filteredUsers.find(
    (u) => u.id === userId,
  );
  const responseTeams = teams; // Use campaign groups directly
  // const responses: Response[] = effectiveResultsData?.instructorResults.availableResponses || []; // Not needed
  const user: User | undefined = selectedUser;

  // Determine role of the selected subject user for question filtering in results view.
  // A user is a team lead if they are the leaderId of any of the available teams.
  const selectedUserIsTeamLead = selectedUser
    ? teams.some((t) => t.leaderId === selectedUser.id)
    : undefined;
  const selectedUserIsInstructor = selectedUser
    ? selectedUser.isInstructor === true
    : undefined;

  const hasPeerQuestions = template?.items.some((item) =>
    isCompatible(ResponseSubjectType.Peer, item.subjectType),
  );
  const hasTeamInstructorQuestions = template?.items.some(
    (item) => item.subjectType === ItemSubjectType.TeamInstructors,
  );

  // When on Peer but form doesn’t support it, redirect to a supported view
  React.useEffect(() => {
    if (!template || subjectType !== ResponseSubjectType.Peer) return;
    if (!hasPeerQuestions && !hasTeamInstructorQuestions) {
      changeUrl({
        changeType: ResponseSubjectType.General,
        changeTeam: "",
        changeUser: "",
      });
    } else if (hasTeamInstructorQuestions && !hasPeerQuestions) {
      changeUrl({ changeType: ResponseSubjectType.TeamInstructors });
    }
  }, [template, hasPeerQuestions, hasTeamInstructorQuestions, subjectType]);

  const showLoading = instructorResultsLoading;

  if (!campaign) {
    return (
      <Layout breadcrumbs={[{ title: "Results" }]} loading={showLoading}>
        {showLoading ? <></> : <>Campaign not found</>}
      </Layout>
    );
  }

  function changeUrl({
    changeCampaign,
    changeType,
    changeTeam,
    changeUser,
  }: {
    changeCampaign?: string;
    changeType?: string;
    changeTeam?: string;
    changeUser?: string;
  }) {
    navigate(
      `/results/${changeCampaign ?? campaignId}/${changeType ?? subjectType}/${
        changeTeam ?? teamId ?? ""
      }/${changeUser ?? userId ?? ""}`,
    );
  }

  const title =
    `${campaign.name} Results` + (user ? ` of ${getUserName(user)}` : "");

  const previewUrl = `/results/preview/${campaignId}/${userId}/results`;

  return (
    <Layout
      actionComponent={
        user && (
          <Button aria-label="preview results" component={Link} to={previewUrl}>
            Preview Released View
          </Button>
        )
      }
      breadcrumbs={[
        {
          title,
        },
      ]}
      loading={showLoading}
    >
      <Box sx={{ mb: 2 }}>
        <CampaignSelector
          campaigns={campaigns as unknown as Campaign[]}
          selected={campaign}
          label={t("surveys.survey")}
          onChange={(target) =>
            changeUrl({
              changeCampaign: target.id,
              changeType: ResponseSubjectType.Peer,
              changeTeam: "",
              changeUser: "",
            })
          }
          sx={{ mr: 2, mb: 2 }}
        />
        <SubjectTypeSelector
          form={template}
          selected={subjectType as ResponseSubjectType}
          label={"Select type"}
          onChange={(target) =>
            changeUrl({
              changeType: target,
              changeTeam: "",
              changeUser: "",
            })
          }
          sx={{ mr: 2, mb: 2 }}
        />
        <TeamSelector
          teams={[{ id: "", name: "Select Team" } as Team, ...teams]}
          selected={team}
          label="Team"
          onChange={(target) =>
            changeUrl({ changeTeam: target.id, changeUser: "" })
          }
          sx={{ mr: 2, mb: 2 }}
        />
        <UserSelector
          users={[
            { id: "", firstName: "Select", lastName: "User" } as User,
            ...filteredUsers,
          ]}
          selected={user}
          label="Users"
          onChange={(target) => changeUrl({ changeUser: target.id })}
          sx={{ mr: 2, mb: 2 }}
        />
        {hasMixedAuthors && (
          <FormControl size="small" sx={{ mr: 2, mb: 2, minWidth: 180 }}>
            <Select
              value={authorType ?? ""}
              onChange={(e) => setAuthorType(e.target.value || undefined)}
              displayEmpty
            >
              <MenuItem value="">All Authors</MenuItem>
              <MenuItem value="instructor">Instructor-Authored</MenuItem>
              <MenuItem value="peer">Peer-Authored</MenuItem>
            </Select>
          </FormControl>
        )}
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography>Feedback Received</Typography>
          <Switch
            checked={reverse}
            onChange={(e) => setReverse(e.target.checked)}
          />
          <Typography>Given</Typography>
        </Stack>
      </Box>
      {template && responseTeams && (
        <AnalysisView
          reverse={reverse}
          subjectType={subjectType as ResponseSubjectType}
          selectedTeamId={teamId}
          selectedUserId={userId}
          template={template}
          user={user}
          selectedUserIsTeamLead={selectedUserIsTeamLead}
          selectedUserIsInstructor={selectedUserIsInstructor}
          teams={responseTeams}
          showStudents={!!team || !!user}
          showQuestions={!!user}
          precomputedAnalyses={precomputedAnalyses}
          precomputedAggregates={precomputedAggregates}
          precomputedPointRows={precomputedPointRows}
          precomputedStudentAverages={studentAverages}
          precomputedTeamAverages={teamAverages}
          onTeamClick={(teamId) =>
            changeUrl({ changeTeam: teamId, changeUser: "" })
          }
          onUserClick={(userId) =>
            changeUrl({ changeTeam: "", changeUser: userId })
          }
          campaign={campaign}
          authorType={authorType}
          hasMultipleRaterTypes={hasMultipleRaterTypes}
        />
      )}
    </Layout>
  );
};
